// Login / register / logout box used by the lobby and admin pages, on the
// kit's /api/auth routes. Renders into `root`, calls `onChange` with the
// account (or null) after every change and once at start.
import { ApiError, api, clear, el } from './api';

export interface AccountInfo {
  id: string;
  name: string;
  role: 'player' | 'admin';
}

interface MeResponse {
  account: AccountInfo | null;
  accounts: boolean; // false when the server runs without a database
}

export interface AuthBoxOptions {
  root: HTMLElement;
  onChange(account: AccountInfo | null): void;
}

export async function installAuthBox(opts: AuthBoxOptions): Promise<void> {
  const { root } = opts;
  let me: MeResponse = { account: null, accounts: false };
  try {
    me = await api<MeResponse>('/api/auth/me');
  } catch {
    // Server unreachable: the page shows itself as guest-only.
  }
  const render = (): void => {
    clear(root);
    if (!me.accounts) {
      root.append(el('span', { class: 'dim' }, 'Playing as guest (accounts are off on this server)'));
      opts.onChange(null);
      return;
    }
    if (me.account) {
      const out = el('button', { type: 'button', class: 'secondary' }, 'Log out');
      out.addEventListener('click', () => {
        void api('/api/auth/logout', 'POST').then(() => {
          me = { ...me, account: null };
          render();
        });
      });
      root.append(el('span', {}, 'Logged in as ', el('b', {}, me.account.name), me.account.role === 'admin' ? ' (admin)' : ''), out);
      opts.onChange(me.account);
      return;
    }
    const name = el('input', { name: 'name', placeholder: 'account name', autocomplete: 'username', maxlength: '16' });
    const pass = el('input', { name: 'password', type: 'password', placeholder: 'password (8+)', autocomplete: 'current-password' });
    const note = el('span', { class: 'note' });
    const go = (path: string) => (e: Event): void => {
      e.preventDefault();
      note.textContent = '';
      void api<{ account: AccountInfo }>(path, 'POST', { name: name.value, password: pass.value })
        .then((r) => {
          me = { ...me, account: r.account };
          render();
        })
        .catch((err: unknown) => {
          note.textContent = err instanceof ApiError ? err.message : 'failed';
        });
    };
    const login = el('button', { type: 'submit' }, 'Log in');
    const register = el('button', { type: 'button', class: 'secondary' }, 'Register');
    login.addEventListener('click', go('/api/auth/login'));
    register.addEventListener('click', go('/api/auth/register'));
    const form = el('form', { class: 'auth' }, name, pass, login, register, note);
    form.addEventListener('submit', go('/api/auth/login'));
    root.append(form);
    opts.onChange(null);
  };
  render();
}
